// OpenTelemetry Web SDK — traces W3C propagados ao backend (Portal Fazenda).
// Ref: conventions/Observability-Rollout-Playbook.md §3 (adaptado de Vite p/ CRA:
// endpoint via REACT_APP_* build-time; init em src/index.tsx).
//
// Exporta OTLP/HTTP para o Ingress externo (browser sai p/ internet):
//   REACT_APP_OTEL_EXPORTER_OTLP_ENDPOINT = https://otlp.clinicadoleite.com.br/v1/traces
//
// Fail-open: qualquer falha (ou endpoint ausente) apenas emite warn e NUNCA
// derruba a aplicação.

import { WebTracerProvider } from "@opentelemetry/sdk-trace-web";
import { BatchSpanProcessor } from "@opentelemetry/sdk-trace-base";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { W3CTraceContextPropagator } from "@opentelemetry/core";
import { Resource } from "@opentelemetry/resources";
import { ZoneContextManager } from "@opentelemetry/context-zone";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { XMLHttpRequestInstrumentation } from "@opentelemetry/instrumentation-xml-http-request";

export const SERVICE_NAME = "cdl-portal-fazenda-web";

// Casa APENAS o host do backend em prd (app-api.clinicadoleite.com.br) e stg
// (app-api.stg.clinicadoleite.com.br) — só nesses hosts injetamos os headers
// W3C (traceparent/tracestate), evitando vazar contexto p/ terceiros.
// Ancorado (^https + / final) para não casar superset como
// `app-api.clinicadoleite.com.br.evil.com` ou `sub.app-api...`.
const PROPAGATE_TRACE_HEADER_CORS_URLS = [
  /^https:\/\/app-api(\.stg)?\.clinicadoleite\.com\.br\//,
  // Ambiente de validação em prod (release canary isolado) — backend em
  // portal-fazenda-api.clinicadoleite.com.br. Necessário p/ o trace e2e
  // frontend→backend propagar nesse host (AB#12574).
  /^https:\/\/portal-fazenda-api\.clinicadoleite\.com\.br\//
];

let initialized = false;

/**
 * Inicializa o tracing OTel do browser. Idempotente e fail-open.
 * Deve ser chamado ANTES do ReactDOM render (ver src/index.tsx).
 */
export function initTracing(): void {
  if (initialized) {
    return;
  }

  const endpoint = process.env.REACT_APP_OTEL_EXPORTER_OTLP_ENDPOINT;
  if (!endpoint) {
    // fail-open: sem endpoint (ex. dev local) não instrumentamos.
    // eslint-disable-next-line no-console
    console.warn(
      "[otel] REACT_APP_OTEL_EXPORTER_OTLP_ENDPOINT ausente — tracing desabilitado."
    );
    return;
  }

  try {
    const provider = new WebTracerProvider({
      resource: new Resource({ "service.name": SERVICE_NAME })
    });

    provider.addSpanProcessor(
      new BatchSpanProcessor(new OTLPTraceExporter({ url: endpoint }))
    );

    provider.register({
      contextManager: new ZoneContextManager(),
      propagator: new W3CTraceContextPropagator()
    });

    registerInstrumentations({
      instrumentations: [
        new FetchInstrumentation({
          propagateTraceHeaderCorsUrls: PROPAGATE_TRACE_HEADER_CORS_URLS,
          clearTimingResources: true
        }),
        new XMLHttpRequestInstrumentation({
          propagateTraceHeaderCorsUrls: PROPAGATE_TRACE_HEADER_CORS_URLS
        })
      ]
    });

    // Web-vitals (LCP/CLS/INP) reportam no momento em que o usuário sai da
    // página — exatamente quando o BatchSpanProcessor ainda não exportou o lote.
    // Força o flush ao esconder/descarregar a página para não perder esses spans.
    const flush = (): void => {
      try {
        provider.forceFlush().catch(() => undefined);
      } catch {
        // fail-open
      }
    };
    // visibilitychange é disparado no `document` (Page Visibility API), não no
    // window — cobre troca de aba / app em background (mobile) sem unload.
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        flush();
      }
    });
    // pagehide é window-level — cobre navegação/fechamento da página.
    window.addEventListener("pagehide", flush);

    initialized = true;
  } catch (err) {
    // fail-open: nunca quebrar o app por causa de observabilidade.
    // eslint-disable-next-line no-console
    console.warn("[otel] falha ao inicializar tracing (ignorado):", err);
  }
}

export default initTracing;
